/**
 * PendingRevenuesPanel — file PMS revenus manuels saisis par les clients (à approuver/rejeter).
 * Batch #14.154 — extracted from AdminPmsQueuePage.jsx during refactor.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { CheckSquare, CheckCircle2, Loader2, X, Trash2 } from "lucide-react";
import { toast } from "@/lib/toast";
import { API, authH } from "./_shared";
import AdminTaskStatusSelector from "@/components/admin/AdminTaskStatusSelector";
import ArchiveButton from "@/components/admin/ArchiveButton";

function RevenueStatusBadge({ status }) {
  const map = {
    pending:  { bg: "bg-amber-100 text-amber-800",   label: "En attente" },
    approved: { bg: "bg-emerald-100 text-emerald-800", label: "Approuvé" },
    rejected: { bg: "bg-rose-100 text-rose-800",     label: "Rejeté" },
  };
  const m = map[status] || map.pending;
  return <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${m.bg}`}>{m.label}</span>;
}

export default function PendingRevenuesPanel({ onUpdate }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [busyId, setBusyId] = useState(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/pms/manual-revenues?status=${statusFilter}`, { headers: authH() });
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);
  useEffect(() => { fetchItems(); }, [fetchItems]);

  const approve = async (id) => {
    setBusyId(id);
    try {
      await axios.post(`${API}/admin/pms/manual-revenues/${id}/approve`, {}, { headers: authH() });
      toast.success("Revenu approuvé et comptabilisé");
      fetchItems();
      onUpdate?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (id) => {
    const reason = window.prompt("Raison du rejet (visible au client) :");
    if (!reason?.trim()) return;
    setBusyId(id);
    try {
      await axios.post(`${API}/admin/pms/manual-revenues/${id}/reject`, { reason: reason.trim() }, { headers: authH() });
      toast.success("Revenu rejeté");
      fetchItems();
      onUpdate?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBusyId(null);
    }
  };

  const deleteRevenue = async (id) => {
    if (!window.confirm("Supprimer définitivement cette ligne ?\n(Elle disparaît de la file ; l'audit reste conservé en backup.)")) return;
    setBusyId(id);
    try {
      await axios.delete(`${API}/admin/pms/manual-revenues/${id}`, { headers: authH() });
      toast.success("Ligne supprimée");
      fetchItems();
      onUpdate?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBusyId(null);
    }
  };

  const total = items.reduce((s, r) => s + Number(r.amount || 0), 0);

  return (
    <>
      <div className="px-5 py-3 border-b bg-slate-50 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <CheckSquare className="w-4 h-4 text-emerald-600" />
          <h2 className="font-semibold text-slate-800 text-sm">Revenus saisis par les clients</h2>
          {items.length > 0 && (
            <span className="text-[11px] font-mono px-2 py-0.5 rounded-full bg-white border text-slate-700">
              Total : {total.toFixed(2)} $
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {[
            { v: "pending",  label: "En attente" },
            { v: "approved", label: "Approuvés" },
            { v: "rejected", label: "Rejetés" },
            { v: "all",      label: "Tous" },
          ].map((s) => (
            <button
              key={s.v}
              onClick={() => setStatusFilter(s.v)}
              className={`text-[11px] px-2 py-0.5 rounded-full transition ${statusFilter === s.v ? "bg-emerald-600 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-100"}`}
              data-testid={`admin-revenues-filter-${s.v}`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div className="p-3">
        {loading ? (
          <div className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin text-slate-300 mx-auto" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-10 text-sm text-slate-400 italic">Aucun revenu {statusFilter !== "all" ? statusFilter : ""}.</div>
        ) : (
          <div className="overflow-x-auto" data-testid="admin-pending-revenues-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase text-slate-500 border-b">
                  <th className="text-left py-2 px-2">Date</th>
                  <th className="text-left py-2 px-2">Master</th>
                  <th className="text-left py-2 px-2">Locataire</th>
                  <th className="text-left py-2 px-2">Apt</th>
                  <th className="text-left py-2 px-2">Mois loyer</th>
                  <th className="text-left py-2 px-2">Méthode</th>
                  <th className="text-right py-2 px-2">Montant</th>
                  <th className="text-center py-2 px-2">Statut</th>
                  <th className="text-right py-2 px-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`admin-pending-revenue-row-${r.id}`}>
                    <td className="py-2 px-2 text-[11px] font-mono text-slate-500">{(r.paid_at || "").slice(0, 10)}</td>
                    <td className="py-2 px-2 text-[12px]">{r._master_name || "—"}</td>
                    <td className="py-2 px-2 text-[12px] font-medium text-slate-800">{r.tenant_name || "—"}</td>
                    <td className="py-2 px-2 text-[11px] text-slate-500">{r.unit_label || "—"}</td>
                    <td className="py-2 px-2 text-[11px] text-slate-500">{r.rent_month_label || "—"}</td>
                    <td className="py-2 px-2 text-[11px] text-slate-500">{r.payment_method}</td>
                    <td className="py-2 px-2 text-right font-mono font-bold text-slate-800">{Number(r.amount).toFixed(2)} $</td>
                    <td className="py-2 px-2 text-center">
                      <RevenueStatusBadge status={r.status} />
                    </td>
                    <td className="py-2 px-2 text-right whitespace-nowrap">
                      {(r.status === "pending" || !r.status) ? (
                        <>
                          <button
                            onClick={() => approve(r.id)}
                            disabled={busyId === r.id}
                            className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-40 mr-1"
                            data-testid={`admin-revenue-approve-${r.id}`}
                          >
                            {busyId === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                            Approuver
                          </button>
                          <button
                            onClick={() => reject(r.id)}
                            disabled={busyId === r.id}
                            className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md bg-white border border-rose-300 text-rose-700 hover:bg-rose-50 disabled:opacity-40"
                            data-testid={`admin-revenue-reject-${r.id}`}
                          >
                            <X className="w-3 h-3" /> Rejeter
                          </button>
                          <button
                            onClick={() => deleteRevenue(r.id)}
                            disabled={busyId === r.id}
                            className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md bg-white border border-slate-300 text-slate-500 hover:bg-slate-100 hover:text-rose-700 disabled:opacity-40 ml-1"
                            title="Supprimer la ligne (erreur de saisie / test)"
                            data-testid={`admin-revenue-delete-${r.id}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                          <AdminTaskStatusSelector
                            itemType="pending_revenue"
                            itemId={r.id}
                            value={r.admin_task_status}
                            onChanged={fetchItems}
                          />
                          <ArchiveButton
                            itemType="pending_revenue"
                            itemId={r.id}
                            itemLabel="ce revenu"
                            onArchived={fetchItems}
                            size="xs"
                          />
                        </>
                      ) : (
                        <span className="text-[10px] text-slate-400">
                          {r.status === "approved" && `Approuvé ${(r.approved_at || "").slice(0, 10)}`}
                          {r.status === "rejected" && `Rejeté ${(r.rejected_at || "").slice(0, 10)}`}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
